import { useEffect, useState } from "react";
import { useSSRHook } from "ssr-hook";

import { useHead, useWakeLock } from "../../services/common";
import { Footer, Header } from "../../Layout";
import { Info, Loading, Page, ShareButton } from "../../components";

import { _ID, Countdown } from "../../../types";
import { Link } from "easy-page-router/react";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";
import { plural, useLang } from "../../services/lang";
import { useHandleError } from "../../services/api";
import { useLocalHistory } from "../../services/localStorage";
import { ThemeDiv } from "../../components/Theme";


export default function CountdownPage({ id }: { id: string }) {
    const { dict } = useLang();

    useLocalHistory("countdown", id);

    const [countdown, error, isLoading, reload] = useSSRHook<Countdown>(`/api/countdown?id=${encodeURIComponent(id)}`);
    useHandleError(error);

    const title = dict.seo.countdown.getTitle(countdown?.title);
    const description = dict.seo.countdown.getDescription(countdown?.title, countdown?.description, countdown?.where);

    useHead({
        title,
        description,
    });

    useWakeLock();

    const time = useTime();

    const diffTime = new Date(countdown?.countdown ?? "").getTime() - time;

    return <Page theme={countdown?.theme} fixed={<>
        <Header title={dict.countdown} link={`/${dict.urlCountdown}`}>
            <Link href={`/${dict.urlCountdown}/${dict.urlNew}`}><Icon path={mdiPlus} size={1} />{dict.createCountdown}</Link>
            <ShareButton title={title} description={description} />
        </Header>
        <Footer />
    </>}>
        <div className={`full-page countdown `}>
            {isLoading && <Loading />}
            {error && <p className="error">{dict.countdownError}</p>}
            {countdown && <>
                <h1>{countdown.title}</h1>
                <Time diffTime={diffTime} />
                {diffTime < 0 && <h3 className="message-after">{countdown.messageAfter || dict.defaultMessageAfter}</h3>}
                <Info when={countdown.countdown} where={countdown.where} />
                {countdown.description && <p className="description">{countdown.description}</p>}
            </>}
        </div>
    </Page>;
}

export function CountdownPreview({ className = "", countdown }: { className?: string, countdown: _ID & Countdown }) {
    const { dict } = useLang();

    const time = useTime();
    const diffTime = new Date(countdown.countdown).getTime() - time;

    const content = <ThemeDiv
        theme={countdown.theme}
        className={`${className} countdown-preview preview `}
    >
        <h3 className="title">{countdown.title}</h3>
        {diffTime >= 0 && <Time diffTime={diffTime} />}
        {diffTime < 0 && <h4>{countdown.messageAfter || dict.defaultMessageAfter}</h4>}
        <Info when={countdown.countdown} where={countdown.where} />
        <p className="description">{countdown.description}</p>
    </ThemeDiv>;

    if (countdown._id) {
        return <Link href={`/${dict.urlCountdown}/${countdown._id}`}>
            {content}
        </Link>
    } else {
        return content
    }
}

export function CountdownTime({ date }: { date: Date | string }) {
    const time = useTime();
    const diffTime = new Date(date).getTime() - time;
    return <Time diffTime={diffTime} />;
}

// return value once per second
export function useTime() {
    const [time, setTime] = useState(() => {
        if (typeof window !== "undefined" && (window as any).__SERVER_TIME__) {
            return Math.floor((window as any).__SERVER_TIME__ / 1000);
        }
        return Math.floor(new Date().getTime() / 1000);
    });

    useEffect(() => {
        const ref = { current: true };
        function tick() {
            requestAnimationFrame(() => {
                const time = Math.floor(new Date().getTime() / 1000);
                if (ref.current) {
                    setTime(time);
                    tick();
                }
            });
        }
        tick();
        return () => {
            ref.current = false;
        }
    }, [time]);

    return time * 1000;
}

export function Time({ diffTime }: { diffTime: number }) {
    const { dict } = useLang();

    const isNegative = diffTime < 0;
    diffTime = Math.abs(diffTime);

    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diffTime / (1000 * 60 * 60)) % 24;
    const minutes = Math.floor(diffTime / (1000 * 60)) % 60;
    const seconds = Math.floor(diffTime / 1000) % 60;

    return <h2 className={`time ${isNegative ? "small" : ""}`} suppressHydrationWarning>
        <span className="number" style={{ display: isNegative ? "inline" : "none" }} suppressHydrationWarning>- </span>
        <span className="number" style={{ display: days > 0 ? "inline" : "none" }} suppressHydrationWarning>{days}</span>
        <span className="number-name" style={{ display: days > 0 ? "inline" : "none" }} suppressHydrationWarning>{plural(dict.day, days)}</span>
        <span className="number" style={{ display: (days > 0 || hours > 0) ? "inline" : "none" }} suppressHydrationWarning>{hours}</span>
        <span className="number-name" style={{ display: (days > 0 || hours > 0) ? "inline" : "none" }} suppressHydrationWarning>{plural(dict.hour, hours)}</span>
        <span className="number" suppressHydrationWarning>{minutes}</span><span className="number-name" suppressHydrationWarning>{plural(dict.minute, minutes)}</span>
        <span className="number" suppressHydrationWarning>{seconds}</span><span className="number-name" suppressHydrationWarning>{plural(dict.second, seconds)}</span>
    </h2>;
}